import { Dispatch, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Menu,
  MenuContent,
  MenuTrigger,
  Separator,
  Text,
  Textarea,
} from '@optiaxiom/react';

import { FeatureFlag } from '@frontend/contexts';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import * as agentStyles from '@frontend/features/agents/style';
import type { SpecializedAgent } from '@frontend/types/specialized-agent';

import { styles } from './AgentFormOutput.css';
import { AgentOutputExamples } from './AgentOutputExamples';

// Update props to use reducer pattern
type AgentFormOutputProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
};

export function AgentFormOutput({ formData, dispatch }: AgentFormOutputProps) {
  // Create a dataTypes object for user-friendly labels
  const dataTypes = {
    string: 'Text',
    number: 'Number',
    boolean: 'Boolean',
    array: 'Array',
    json: 'Object',
  } as const;

  // Helper to update nested output fields
  const updateOutputField = (outputUpdates: Partial<NonNullable<SpecializedAgent['output']>>) => {
    dispatch({ type: 'UPDATE_OUTPUT', value: outputUpdates });
  };

  // Local state for schema input and error
  const [schemaInput, setSchemaInput] = useState<string>(
    typeof formData.output?.schema === 'string'
      ? formData.output.schema
      : formData.output?.schema
        ? JSON.stringify(formData.output.schema, null, 2)
        : ''
  );
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Sync local state with formData changes
  useEffect(() => {
    if (typeof formData.output?.schema === 'string') {
      setSchemaInput(formData.output.schema);
    } else if (formData.output?.schema) {
      setSchemaInput(JSON.stringify(formData.output.schema, null, 2));
    } else {
      setSchemaInput('');
    }
  }, [formData.output?.schema]);

  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full">
          <CardHeader>
            <DisclosureTrigger>
              <Text>Output</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Flex
                flexDirection="row"
                gap="16"
                pb="8"
                w="full"
                className={agentStyles.FormInputFieldMaxWidth}
              >
                <Field
                  label="Data Type"
                  info="Specifies the format of the output after this agent executes"
                  required
                >
                  <Menu
                    options={Object.entries(dataTypes).map(([type, label]) => ({
                      label,
                      execute: () => updateOutputField({ type }),
                      selected: formData.output?.type === type,
                    }))}
                  >
                    <MenuTrigger>
                      {dataTypes[formData.output?.type as keyof typeof dataTypes] || 'Text'}
                    </MenuTrigger>
                    <MenuContent />
                  </Menu>
                </Field>
                <Field label="Description" style={{ flex: 1 }}>
                  <Input
                    className={styles.outputField}
                    value={formData.output?.description || ''}
                    placeholder="Describe the output of your agent"
                    onChange={e => updateOutputField({ description: e.target.value })}
                  />
                </Field>
              </Flex>
              {formData.output?.type === 'json' && (
                <Field label="Output Schema" info="JSON schema for the output (optional)">
                  <Flex
                    flexDirection="column"
                    gap="4"
                    className={agentStyles.FormInputFieldMaxWidth}
                  >
                    <Textarea
                      className={styles.outputSchemaField}
                      value={schemaInput}
                      placeholder="Describe the output schema here"
                      onChange={e => {
                        setSchemaInput(e.target.value);
                        updateOutputField({ schema: e.target.value });
                        setSchemaError(null); // Clear error on change
                      }}
                      onBlur={() => {
                        if (schemaInput.trim() === '') {
                          updateOutputField({ schema: '' });
                          setSchemaError(null);
                          return;
                        }
                        try {
                          const parsed = JSON.parse(schemaInput);
                          if (
                            typeof parsed === 'object' &&
                            parsed !== null &&
                            !Array.isArray(parsed)
                          ) {
                            updateOutputField({ schema: parsed });
                            setSchemaError(null);
                          } else {
                            setSchemaError('Schema must be a valid JSON dictionary/object.');
                            updateOutputField({ schema: schemaInput });
                          }
                        } catch {
                          setSchemaError('Schema must be a valid JSON dictionary/object.');
                          updateOutputField({ schema: schemaInput });
                        }
                      }}
                      rows={4}
                      resize="vertical"
                    />
                    {schemaError && (
                      <span style={{ color: 'red', fontSize: '0.9em' }}>{schemaError}</span>
                    )}
                  </Flex>
                </Field>
              )}

              {formData.id && (
                <FeatureFlag featureKey="agent_evaluation">
                  <Separator />
                  <AgentOutputExamples agentId={formData.id} agent={formData} dispatch={dispatch} />
                </FeatureFlag>
              )}
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
